import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { UrlConstantsService } from 'src/app/services/url-constants.service';

interface DayData {
  date: string;
  count: number;
}

interface TopPage {
  slug: string;
  title: string;
  count: number;
}

interface RecentUser {
  email: string;
  name: string;
  last_login: string | null;
}

interface AdminStats {
  views_24h: number;
  views_30d: number;
  checks_24h: number;
  checks_30d: number;
  top_pages: TopPage[];
  views_daily: DayData[];
  checks_daily: DayData[];
  recent_users: RecentUser[];
}

interface PlatformState {
  name: string;
  data: unknown;
  description: string;
  known: boolean;
  updated_at: string | null;
}

interface Bar {
  x: number;
  y: number;
  width: number;
  height: number;
  data: DayData;
}

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss'],
  standalone: false,
})
export class AdminDashboardPageComponent implements OnInit {
  stats: AdminStats | null = null;
  loading = true;
  error = false;

  platformStates = signal<PlatformState[] | null>(null);
  platformStatesError = signal(false);
  savingState = signal<string | null>(null);

  tooltipViewsBar: Bar | null = null;
  tooltipChecksBar: Bar | null = null;

  readonly chartWidth = 720;
  readonly chartHeight = 180;
  readonly paddingLeft = 44;
  readonly paddingRight = 8;
  readonly paddingTop = 10;
  readonly paddingBottom = 28;

  constructor(
    private http: HttpClient,
    private urls: UrlConstantsService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.loadPlatformStates();
    try {
      this.stats = await lastValueFrom(this.http.get<AdminStats>(this.urls.ADMIN_DASHBOARD_STATS));
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private async loadPlatformStates() {
    try {
      this.platformStates.set(await lastValueFrom(this.http.get<PlatformState[]>(this.urls.ADMIN_PLATFORM_STATES)));
    } catch {
      this.platformStatesError.set(true);
    }
  }

  isBoolean(state: PlatformState): boolean {
    return typeof state.data === 'boolean';
  }

  // Lets long snake_case names wrap at underscores rather than mid-word.
  breakable(name: string): string {
    return name.replace(/_/g, '_\u200b');
  }

  formatData(data: unknown): string {
    return JSON.stringify(data);
  }

  async toggle(state: PlatformState) {
    const next = !state.data;
    if (!next && !confirm(`Turn off ${state.name}?\n\n${state.description}`)) return;

    this.savingState.set(state.name);
    try {
      const updated = await lastValueFrom(
        this.http.patch<PlatformState>(this.urls.ADMIN_PLATFORM_STATE(state.name), { data: next }));
      this.platformStates.update(list => list?.map(s => s.name === updated.name ? updated : s) ?? null);
    } catch {
      alert(`Failed to update ${state.name}.`);
    } finally {
      this.savingState.set(null);
    }
  }

  private makeBars(data: DayData[]): Bar[] {
    if (!data?.length) return [];
    const plotW = this.chartWidth - this.paddingLeft - this.paddingRight;
    const plotH = this.chartHeight - this.paddingTop - this.paddingBottom;
    const max = Math.max(...data.map(d => d.count), 1);
    const barWidth = plotW / data.length;
    const gap = barWidth * 0.18;
    return data.map((d, i) => {
      const barH = (d.count / max) * plotH;
      return {
        x: this.paddingLeft + i * barWidth + gap / 2,
        y: this.paddingTop + plotH - barH,
        width: barWidth - gap,
        height: Math.max(barH, 1),
        data: d,
      };
    });
  }

  private makeYTicks(data: DayData[]): { value: number; y: number }[] {
    const plotH = this.chartHeight - this.paddingTop - this.paddingBottom;
    const max = Math.max(...(data?.map(d => d.count) ?? [0]), 1);
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => ({
      value: Math.round((max * i) / steps),
      y: this.paddingTop + plotH - (i / steps) * plotH,
    }));
  }

  private makeXLabels(data: DayData[]): { label: string; x: number }[] {
    if (!data?.length) return [];
    const plotW = this.chartWidth - this.paddingLeft - this.paddingRight;
    const barWidth = plotW / data.length;
    return data
      .filter((_, i) => i % 7 === 0 || i === data.length - 1)
      .map(d => ({
        label: this.formatShort(d.date),
        x: this.paddingLeft + data.indexOf(d) * barWidth + barWidth / 2,
      }));
  }

  get viewsBars(): Bar[] { return this.makeBars(this.stats?.views_daily ?? []); }
  get checksBars(): Bar[] { return this.makeBars(this.stats?.checks_daily ?? []); }
  get viewsYTicks() { return this.makeYTicks(this.stats?.views_daily ?? []); }
  get checksYTicks() { return this.makeYTicks(this.stats?.checks_daily ?? []); }
  get viewsXLabels() { return this.makeXLabels(this.stats?.views_daily ?? []); }
  get checksXLabels() { return this.makeXLabels(this.stats?.checks_daily ?? []); }

  formatShort(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  }

  tooltipX(bar: Bar): number { return bar.x + bar.width / 2 - 44; }
  tooltipY(bar: Bar): number { return bar.y - 38; }
  tooltipTextX(bar: Bar): number { return bar.x + bar.width / 2; }

  formatLoginDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en', { dateStyle: 'medium', timeStyle: 'short' });
  }
}
